/**
 * Vehicle Compatibility — client-side check.
 *
 * Reads the saved vehicle specs from localStorage and checks compatibility
 * against the SELECTED VARIANT (not the product-level PCD list). Listens
 * for both `vehicle:changed` and Horizon's `variant:update` event so the
 * compatibility badge re-renders whenever the user picks a different variant.
 *
 * Bore values are fetched per PCD from the API because bore varies
 * by bolt pattern, but Shopify metafields only store one value.
 *
 * Shows compact detail rows: compatible/not compatible + hub-centric ring note.
 */

import { getVehicle } from './vehicle-store.js';

const ET_TOLERANCE = 15;
const API_BASE = 'https://static.218.250.90.157.clients.your-server.de';

class VehicleCompatibilityElement extends HTMLElement {
  connectedCallback() {
    this._okRow = this.querySelector('[data-state="ok"]');
    this._failRow = this.querySelector('[data-state="fail"]');
    this._emptyRow = this.querySelector('[data-state="empty"]');
    this._noVehicleRow = this.querySelector('[data-state="no-vehicle"]');
    this._ringRow = this.querySelector('[data-role="ring-note"]');
    this._ringSizeEl = this.querySelector('[data-role="ring-size"]');
    this._ringPreciseRow = this.querySelector('[data-role="ring-precise"]');
    this._ringOkRow = this.querySelector('[data-role="ring-ok"]');
    this._etWarnRow = this.querySelector('[data-role="et-warn"]');
    this._etWarnDetail = this.querySelector('[data-role="et-warn-detail"]');
    this._etOkRow = this.querySelector('[data-role="et-ok"]');
    this._etOkDetail = this.querySelector('[data-role="et-ok-detail"]');
    this._boreByPcd = null;

    // Parse variant JSON for variant-level checks
    const script = document.querySelector('script[data-product-variants]');
    this._variants = [];
    if (script) {
      try {
        this._variants = JSON.parse(script.textContent || '[]');
      } catch (e) {
        console.warn('VehicleCompatibility: failed to parse variants', e);
      }
    }

    this._filterNoMatch = false;
    this._pickedEt = null;

    // Listen for bore:loaded from wheel-configurator (merged product-data call)
    window.addEventListener('bore:loaded', (e) => {
      if (e.detail?.boreByPcd) {
        this._boreByPcd = {};
        for (const [pcd, data] of Object.entries(e.detail.boreByPcd)) {
          this._boreByPcd[pcd] = data;
        }
        this._updateBoreAttributes();
        this._render();
        // Reveal now that we have bore data
        if (!this.hasAttribute('data-ready')) {
          clearTimeout(this._readyTimeout);
          this.setAttribute('data-ready', '');
        }
      }
    });

    // If configurator is on the page, it will dispatch bore:loaded — wait briefly as fallback
    if (document.querySelector('wheel-configurator')) {
      this._boreTimeout = setTimeout(() => {
        if (!this._boreByPcd || Object.keys(this._boreByPcd).length === 0) {
          this._loadBoreData();
        }
      }, 3000);
    } else {
      this._loadBoreData();
    }

    window.addEventListener('vehicle:changed', () => this._render());
    window.addEventListener('et-picker:changed', (e) => {
      this._pickedEt = e.detail?.et ?? null;
      this._render();
    });
    window.addEventListener('vehicle-filter:no-match', () => {
      // Fully Forged Aventador Wheels are made to order for the selected car.
      // Do not let a missing ready-made API variant create a false fail badge.
      const madeToOrderAventador = !!document.querySelector(
        'wheel-configurator[data-aventador]:not([data-hybrid-forged])'
      );
      this._filterNoMatch = !madeToOrderAventador;
      this._render();
    });
    window.addEventListener('vehicle-filter:match', () => {
      this._filterNoMatch = false;
      this._render();
    });
    window.addEventListener('vehicle-filter:cleared', () => {
      this._filterNoMatch = false;
      this._render();
    });

    // Listen for Horizon's variant:update to re-check with selected variant
    const sectionRoot = this.closest('.shopify-section, dialog');
    if (sectionRoot) {
      this._variantUpdateHandler = () => {
        requestAnimationFrame(() => this._render());
      };
      sectionRoot.addEventListener('variant:update', this._variantUpdateHandler);
      this._sectionRoot = sectionRoot;
    }

    // Initial render (won't be visible yet due to CSS hiding :not([data-ready]))
    this._render();

    // If no vehicle saved, reveal immediately (no bore data needed)
    const vehicle = getVehicle();
    if (!vehicle || !vehicle.specs) {
      this.setAttribute('data-ready', '');
    } else {
      // Defer visibility until bore data arrives (prevents flash of wrong state)
      this._readyTimeout = setTimeout(() => {
        this._render();
        this.setAttribute('data-ready', '');
      }, 2000);
    }
  }

  disconnectedCallback() {
    if (this._boreTimeout) clearTimeout(this._boreTimeout);
    if (this._readyTimeout) clearTimeout(this._readyTimeout);
    if (this._sectionRoot && this._variantUpdateHandler) {
      this._sectionRoot.removeEventListener('variant:update', this._variantUpdateHandler);
    }
  }

  async _loadBoreData() {
    const handle = this.dataset.productHandle;
    if (!handle) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/wheels/product-bore?handle=${encodeURIComponent(handle)}`
      );
      if (!res.ok) return;
      const json = await res.json();
      if (json.success && json.data) {
        this._boreByPcd = {};
        for (const row of json.data) {
          this._boreByPcd[row.pcd.trim().toLowerCase()] = {
            center_bore: row.center_bore || 0,
            center_bore_max: row.center_bore_max || 0,
          };
        }
        // Update data attributes with PCD-specific bore for the current vehicle
        this._updateBoreAttributes();
        // Re-render with correct bore data
        this._render();
        // Notify other components (e.g. variant filter, wheel-specs) that bore data is ready
        window.dispatchEvent(new CustomEvent('bore:loaded', { detail: { boreByPcd: this._boreByPcd } }));
        // Reveal now that we have bore data
        if (!this.hasAttribute('data-ready')) {
          clearTimeout(this._readyTimeout);
          this.setAttribute('data-ready', '');
        }
      }
    } catch (e) {
      console.warn('VehicleCompatibility: failed to load bore data', e);
      // Show even on error (timeout will handle it, but be safe)
      if (!this.hasAttribute('data-ready')) {
        clearTimeout(this._readyTimeout);
        this.setAttribute('data-ready', '');
      }
    }
  }

  /** Update data-center-bore attributes with the PCD-specific bore for the current vehicle. */
  _updateBoreAttributes() {
    if (!this._boreByPcd) return;
    const vehicle = getVehicle();
    const pcd = vehicle?.specs?.pcd;
    if (!pcd) return;
    const pcdKey = pcd.trim().toLowerCase();
    const pcdBore = this._boreByPcd[pcdKey];
    if (pcdBore) {
      this.dataset.centerBore = String(pcdBore.center_bore);
      this.dataset.centerBoreMax = String(pcdBore.center_bore_max);
    }
  }

  _render() {
    const vehicle = getVehicle();
    this._hideAll();

    if (!vehicle) {
      this._show(this._noVehicleRow || this._emptyRow);
      return;
    }

    const specs = vehicle.specs;

    // If no specs saved yet, show compatible optimistically
    if (!specs) {
      this._show(this._okRow);
      return;
    }

    // If variant filter determined zero compatible variants, show fail.
    // Fully Forged Aventador Wheels are an exception because they are
    // machined to the selected vehicle rather than supplied as a fixed fitment.
    const madeToOrderAventador = !!document.querySelector(
      'wheel-configurator[data-aventador]:not([data-hybrid-forged])'
    );
    if (this._filterNoMatch && !madeToOrderAventador) {
      this._show(this._failRow);
      return;
    }

    const selectedVariant = this._getSelectedVariant();
    const result = this._checkCompatibility(specs, selectedVariant);
    if (result.compatible) {
      this._show(this._okRow);
      if (result.etWarning && this._etWarnRow) {
        if (this._etWarnDetail) {
          this._etWarnDetail.textContent = `ET utenfor anbefalt verdi: ET${result.etRecMin}-${result.etRecMax}`;
        }
        this._show(this._etWarnRow);
      } else if (result.etRecMax > 0 && this._etOkRow) {
        // ET is within recommended range — show positive confirmation
        if (this._etOkDetail) {
          this._etOkDetail.textContent = `ET innenfor anbefalt verdi: ET${result.etRecMin}-${result.etRecMax}`;
        }
        this._show(this._etOkRow);
      }
      if (result.needsRing) {
        if (this._ringSizeEl) {
          this._ringSizeEl.textContent = `(${result.wheelBore}mm \u2192 ${result.vehicleBore}mm)`;
        }
        this._show(this._ringRow);
      } else if (result.borePreciseMatch) {
        this._show(this._ringPreciseRow);
      } else if (result.boreChecked) {
        this._show(this._ringOkRow);
      }
    } else {
      this._show(this._failRow);
    }
  }

  /** Read selected option values from the picker DOM and find the matching variant. */
  _getSelectedVariant() {
    if (this._variants.length === 0) return null;

    const section = this.closest('.shopify-section') || document;
    const picker = section.querySelector('variant-picker') || document.querySelector('variant-picker');
    if (!picker) return null;

    const selectedOptions = [];
    for (let i = 0; i < 3; i++) {
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${i}"]`);
      if (fieldset) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) {
          selectedOptions[i] = checked.value;
          continue;
        }
      }
      const selects = picker.querySelectorAll('select.variant-option__select');
      if (selects[i]) {
        selectedOptions[i] = selects[i].value;
      }
    }

    // Find variant matching all selected options
    return this._variants.find((v) => {
      if (selectedOptions[0] && v.option1 !== selectedOptions[0]) return false;
      if (selectedOptions[1] && v.option2 !== selectedOptions[1]) return false;
      if (selectedOptions[2] && v.option3 !== selectedOptions[2]) return false;
      return true;
    }) || null;
  }

  _checkCompatibility(specs, selectedVariant) {
    // When a variant is selected, use its specific option values
    if (selectedVariant) {
      return this._checkVariantCompatibility(specs, selectedVariant);
    }

    // Fallback: product-level data attributes (no variant selected)
    return this._checkProductCompatibility(specs);
  }

  /** Check compatibility using the selected variant's specific PCD/ET/size. */
  _checkVariantCompatibility(specs, variant) {
    // 1. PCD match — variant's option1 is a single PCD value
    const variantPcd = (variant.option1 || '').trim().toLowerCase();
    if (specs.pcd && variantPcd) {
      const vehiclePcd = specs.pcd.trim().toLowerCase();
      if (variantPcd !== vehiclePcd && variantPcd !== 'custom') {
        return { compatible: false, needsRing: false };
      }
    }

    // 2. ET check — parse from variant's option3, override with ET picker selection
    const vehicleEt = specs.et_front ?? specs.et;
    const etStr = (variant.option3 || '').replace(/\s*\(.*\)/, '');
    const etMatch = etStr.match(/ET(-?\d+)(?:-(-?\d+))?/);
    let etWarning = false;
    let etRecMin = 0;
    let etRecMax = 0;
    if (vehicleEt != null && etMatch) {
      let etMin = parseInt(etMatch[1], 10);
      let etMax = etMatch[2] ? parseInt(etMatch[2], 10) : etMin;
      // If user picked a specific ET from the dropdown, use that instead of the range.
      // Also read the ET picker DOM directly as a fallback (handles page-load timing).
      const pickedEt = this._pickedEt ?? this._readEtPicker();
      if (pickedEt != null) {
        etMin = pickedEt;
        etMax = pickedEt;
      }
      const hasApiData = specs.et_min_front > 0 && specs.et_max_front > 0;
      if (hasApiData) {
        etRecMin = specs.et_min_front;
        etRecMax = specs.et_max_front;
        // API data available — soft warn only, never hard block
        if (etMin > etRecMax || etMax < etRecMin) {
          etWarning = true;
        }
      } else {
        // No API data — use ±15 as hard limit (safety net)
        const hardMin = vehicleEt - ET_TOLERANCE;
        const hardMax = vehicleEt + ET_TOLERANCE;
        if (etMin > hardMax || etMax < hardMin) {
          return { compatible: false, needsRing: false };
        }
      }
    }

    // 3. Diameter — parse from variant's option2
    const vehicleDiameter = specs.diameter_front ?? specs.oem_diameter;
    const sizeStr = variant.option2 || '';
    const diamMatch = sizeStr.match(/^(\d+(?:\.\d+)?)/);
    if (vehicleDiameter > 0 && diamMatch) {
      const diameter = parseFloat(diamMatch[1]);
      if (diameter > 0 && Math.abs(diameter - vehicleDiameter) > 2) {
        return { compatible: false, needsRing: false };
      }
    }

    // 4. Bore — use PCD-specific bore from the variant's PCD
    //    Skip bore check entirely for custom-bore products (e.g. Aventador Forged)
    const isCustomBore = this.hasAttribute('data-custom-bore');
    let boreFinal = parseFloat(this.dataset.centerBore) || 0;
    let boreMaxFinal = parseFloat(this.dataset.centerBoreMax) || 0;
    if (!isCustomBore && this._boreByPcd && variantPcd) {
      const pcdBore = this._boreByPcd[variantPcd];
      if (pcdBore) {
        boreFinal = pcdBore.center_bore;
        boreMaxFinal = pcdBore.center_bore_max;
      }
    }

    const vehicleBore = specs.center_bore;
    let needsRing = false;
    let borePreciseMatch = false;
    let boreChecked = false;
    if (!isCustomBore && vehicleBore > 0) {
      const compatBore = boreMaxFinal > 0 ? boreMaxFinal : boreFinal;
      const displayBore = boreFinal > 0 ? boreFinal : boreMaxFinal;
      if (compatBore > 0) {
        boreChecked = true;
        if (compatBore < vehicleBore) {
          return { compatible: false, needsRing: false, boreChecked: true };
        }
        if (displayBore - vehicleBore > 0.5) {
          needsRing = true;
        } else if (displayBore > 0 && displayBore >= vehicleBore) {
          borePreciseMatch = true;
        }
      }
    }

    return {
      compatible: true,
      needsRing,
      borePreciseMatch,
      boreChecked,
      etWarning,
      etRecMin,
      etRecMax,
      wheelBore: needsRing ? (boreFinal > 0 ? boreFinal : boreMaxFinal) : 0,
      vehicleBore: needsRing ? vehicleBore : 0,
    };
  }

  /** Fallback: check compatibility using product-level data attributes. */
  _checkProductCompatibility(specs) {
    const isCustomBore = this.hasAttribute('data-custom-bore');
    const pcdListRaw = this.dataset.pcdList || '';
    const productPcds = pcdListRaw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const productBore = parseFloat(this.dataset.centerBore) || 0;
    const productBoreMax = parseFloat(this.dataset.centerBoreMax) || 0;
    const productEtMin = parseFloat(this.dataset.etMin);
    const productEtMax = parseFloat(this.dataset.etMax);
    const productSize = parseFloat(this.dataset.size) || 0;

    // 1. PCD match
    if (specs.pcd) {
      const vehiclePcd = specs.pcd.trim().toLowerCase();
      if (productPcds.length > 0) {
        const pcdMatch = productPcds.some(
          (p) => p === vehiclePcd || p === 'custom'
        );
        if (!pcdMatch) return { compatible: false, needsRing: false };
      } else if (this._variants.length > 0) {
        // No product-level PCD list — check variant PCDs instead
        const anyMatch = this._variants.some(v => {
          const vPcd = (v.option1 || '').trim().toLowerCase();
          return vPcd === vehiclePcd || vPcd === 'custom';
        });
        if (!anyMatch) return { compatible: false, needsRing: false };
      }
    }

    // 2. ET range overlap — override with picked ET from configurator slider
    const vehicleEt = specs.et_front ?? specs.et;
    let etWarning = false;
    let etRecMin = 0;
    let etRecMax = 0;
    if (vehicleEt != null && !isNaN(productEtMin) && !isNaN(productEtMax)) {
      let checkEtMin = productEtMin;
      let checkEtMax = productEtMax;
      const pickedEt = this._pickedEt ?? this._readEtPicker();
      if (pickedEt != null) {
        checkEtMin = pickedEt;
        checkEtMax = pickedEt;
      }
      const hasApiData = specs.et_min_front > 0 && specs.et_max_front > 0;
      if (hasApiData) {
        etRecMin = specs.et_min_front;
        etRecMax = specs.et_max_front;
        // API data available — soft warn only, never hard block
        if (checkEtMin > etRecMax || checkEtMax < etRecMin) {
          etWarning = true;
        }
      } else {
        // No API data — use ±15 as hard limit (safety net)
        const hardMin = vehicleEt - ET_TOLERANCE;
        const hardMax = vehicleEt + ET_TOLERANCE;
        if (checkEtMin > hardMax || checkEtMax < hardMin) return { compatible: false, needsRing: false };
      }
    }

    // 3. Center bore — skip for custom-bore products (e.g. Aventador Forged)
    let boreFinal = productBore;
    let boreMaxFinal = productBoreMax;
    if (!isCustomBore && this._boreByPcd && specs.pcd) {
      const pcdKey = specs.pcd.trim().toLowerCase();
      const pcdBore = this._boreByPcd[pcdKey];
      if (pcdBore) {
        boreFinal = pcdBore.center_bore;
        boreMaxFinal = pcdBore.center_bore_max;
      }
    }

    const vehicleBore = specs.center_bore;
    let needsRing = false;
    let borePreciseMatch = false;
    let boreChecked = false;
    if (!isCustomBore && vehicleBore > 0) {
      const compatBore = boreMaxFinal > 0 ? boreMaxFinal : boreFinal;
      const displayBore = boreFinal > 0 ? boreFinal : boreMaxFinal;
      if (compatBore > 0) {
        boreChecked = true;
        if (compatBore < vehicleBore) {
          return { compatible: false, needsRing: false, boreChecked: true };
        }
        if (displayBore - vehicleBore > 0.5) {
          needsRing = true;
        } else if (displayBore > 0 && displayBore >= vehicleBore) {
          borePreciseMatch = true;
        }
      }
    }

    // 4. Diameter
    const vehicleDiameter = specs.diameter_front ?? specs.oem_diameter;
    if (vehicleDiameter > 0 && productSize > 0) {
      if (Math.abs(productSize - vehicleDiameter) > 2) {
        return { compatible: false, needsRing: false, boreChecked };
      }
    }

    return {
      compatible: true,
      needsRing,
      borePreciseMatch,
      boreChecked,
      etWarning,
      etRecMin,
      etRecMax,
      wheelBore: needsRing ? (boreFinal > 0 ? boreFinal : boreMaxFinal) : 0,
      vehicleBore: needsRing ? vehicleBore : 0,
    };
  }

  /** Read the current ET value from the ET picker select DOM (fallback for page-load timing). */
  _readEtPicker() {
    const select = document.querySelector('et-picker select');
    if (!select) return null;
    const val = parseInt((select.value || '').replace(/\D/g, ''), 10);
    return isNaN(val) ? null : val;
  }

  _hideAll() {
    this._okRow.hidden = true;
    this._failRow.hidden = true;
    this._emptyRow.hidden = true;
    if (this._noVehicleRow) this._noVehicleRow.hidden = true;
    if (this._etWarnRow) this._etWarnRow.hidden = true;
    if (this._etOkRow) this._etOkRow.hidden = true;
    this._ringRow.hidden = true;
    if (this._ringPreciseRow) this._ringPreciseRow.hidden = true;
    if (this._ringOkRow) this._ringOkRow.hidden = true;
  }

  _show(el) {
    if (el) el.hidden = false;
  }
}

if (!customElements.get('vehicle-compatibility')) {
  customElements.define('vehicle-compatibility', VehicleCompatibilityElement);
}
